import { DispatchCard } from 'src/entities/dispatch';

import { isAssignedDelivery } from './assignedDelivery';

/**
 * VehicleRowCardVM から必要なものだけ抜いた中立型。
 * kind は 4 値のまま受ける（2 値に潰すと template / no_delivery を突合前に落とせない）。
 * lat / lng は operation が持たないので呼び出し側が legRun から入れる
 * （積なら loading_lat/lng、降なら unloading_lat/lng。buildPlaceKey と同じ導出）。
 */
export type OperationSourceCardInput = {
  placeKey: string;
  kind: 'loading' | 'unloading' | 'no_delivery' | 'template';
  placeName: string;
  operations: readonly {
    id: number;
    action: '積' | '降';
    arrivalAt: string;
    departureAt: string;
    operationDurationSeconds: number;
    waitingDurationSeconds: number;
    drivingDistanceMm: number;
    shipmentOrderLegRunId: number;
    lat: number | null;
    lng: number | null;
  }[];
  legRuns: readonly {
    id?: number;
    item_count: number;
    item_total_weight_kg: number;
    item_total_volume_m3: number;
    shipment_order_id?: number;
  }[];
};

/** 1 停車。複数 operation（= 複数 legRun）がまとまる */
export type OperationPlace = {
  /** React key。構成 operation の id 由来で地点ごとに一意 */
  placeId: string;
  operationIds: number[];
  kind: 'loading' | 'unloading';
  locationName: string;
  /** 地図マーカーと共用。引けなければ null */
  position: [number, number] | null;
  /** 先頭 operation の到着 */
  arrivalAt: string;
  /** 末尾 operation の出発 */
  departureAt: string;
  /** 構成 operation の作業時間の合計 */
  workSeconds: number;
  /** 構成 operation の待機の合計。/plans の PlanningsOperationPlace:324 と同じ */
  waitSeconds: number;
  /** この地点で扱う legRun 群。数量表示に使う */
  shipmentOrderLegRunIds: number[];
  /** 詳細選択で集約カードを解決する */
  cardIndex: number;
  /** 既存 DispatchSelection へ渡す集約カード順 */
  dispatchOrderIndex: number;
};

export type LegRunQuantityMap = ReadonlyMap<number, {
  itemCount: number;
  weightKg: number;
  volumeM3: number;
  /** 追加。invoiceCount を distinct で数えるのに使う */
  shipmentOrderId: number | null;
}>;

const matchKey = (kind: 'loading' | 'unloading', placeKey: string) => `${kind}-${placeKey}`;

/** 突合済みの 1 operation。地点集約の入力 */
type MatchedOperation = OperationSourceCardInput['operations'][number] & {
  kind: 'loading' | 'unloading';
  locationName: string;
  cardIndex: number;
  dispatchOrderIndex: number;
};

/**
 * operation を「実際の停車」単位にまとめる。
 *
 * ## なぜ operation 単位ではないのか
 * operation は訪問ではなく legRun の単位で作られる（API の create_operation が
 * leg_run ごとに 積1+降1 を作る）。1 積地で 8 件積む車両は積 operation が 8 本になり、
 * 素直に並べると同じ地点の行が 8 本出る。
 *
 * ## 集約規則は /plans の移植
 * PlanningsOperationGroup.model.ts:165-178 と同じく、arrivalAt 昇順に走査して
 * 「departureAt が変わった」または「走行があって座標が変わった」ところで地点を切る。
 * キーに running index を含めるので、同じ座標への再訪は別地点として残る。
 *
 * ## cardIndex の変化でも切る理由
 * 手動配車・固定ルート適用は driving_distance_mm: 0 で operation を作り
 * (apply_to_existing_planning_service.rb:652)、同ファイルの pair_times_from_chain
 * (:615-623) は 1 つの base_time から load/unload の時刻を導出する。そのため
 * staying_seconds が同じ2店舗は arrivalAt・departureAt が完全に一致しうる。
 * departureAt と座標変化だけで切ると、この場合に後続の店舗が先頭の店舗へ
 * 吸収されて画面から消える。cardIndex は (kind, placeKey) から決まるので、
 * 別カードに切り替わったら必ず地点も切る。DC の 8 legRun は同じカードなので
 * 従来どおりまとまり、同一座標への再訪は同じカードのまま departureAt の差で
 * 従来どおり分かれる（groupOpsToCards (vehicleRowVM.ts:154-166) が非連続でも
 * 同地点・同 action を 1 カードにマージするため、再訪はカードでは区別できない）。
 */
export function buildOperationPlaces(
  sourceCards: readonly OperationSourceCardInput[],
  cards: readonly DispatchCard[],
): { places: OperationPlace[]; quantities: LegRunQuantityMap } {
  const empty = { places: [], quantities: new Map() };

  // --- 1. 既存 visibleCards（VehicleRowView.tsx:64-71）と同じ規則で id 重複排除 ---
  const seen = new Set<string>();
  const visible: { card: DispatchCard; cardIndex: number }[] = [];
  cards.forEach((card, cardIndex) => {
    if (seen.has(card.id)) return;
    seen.add(card.id);
    visible.push({ card, cardIndex });
  });

  // --- 2. 採番 Map と対応キー Map を同じ visible 配列から作る ---
  let deliveryOrder = 0;
  const dispatchOrderByCardIndex = new Map<number, number>();
  const cardIndexByKey = new Map<string, number>();
  const ambiguousKeys = new Set<string>();
  visible.forEach(({ card, cardIndex }) => {
    dispatchOrderByCardIndex.set(cardIndex, card.type === 'delivery' ? ++deliveryOrder : 0);
    if (card.placeKey == null) return;
    const key = matchKey(card.type === 'loading' ? 'loading' : 'unloading', card.placeKey);
    // FR 由来カードは同 row 内で同じ placeKey を 2 枚生成しうる（vehicleRowVM.ts:88-94）
    if (cardIndexByKey.has(key)) ambiguousKeys.add(key);
    else cardIndexByKey.set(key, cardIndex);
  });

  // --- 3. source を突き合わせて operation を平坦化する ---
  const matched: MatchedOperation[] = [];
  for (const source of sourceCards) {
    if (source.kind === 'no_delivery' || source.kind === 'template') continue;

    const key = matchKey(source.kind, source.placeKey);
    if (ambiguousKeys.has(key)) return empty;
    const cardIndex = cardIndexByKey.get(key);
    if (cardIndex == null) return empty;
    const dispatchOrderIndex = dispatchOrderByCardIndex.get(cardIndex);
    if (dispatchOrderIndex == null) return empty;

    if (source.kind === 'unloading' && !isAssignedDelivery(cards[cardIndex])) continue;

    for (const operation of source.operations) {
      matched.push({
        ...operation,
        kind: source.kind,
        locationName: source.placeName,
        cardIndex,
        dispatchOrderIndex,
      });
    }
  }

  // --- 4. arrivalAt → departureAt 昇順にしてから地点へ集約する（/plans と同じ規則。
  //        PlanningsOperationGroup.model.ts:56-65）。集約は departureAt の変化で切るため、
  //        arrivalAt が同値のときに入力順へ落ちると地点の切れ方と seriesIndex が
  //        入力順に依存してしまう ---
  matched.sort((a, b) => {
    const byArrival = new Date(a.arrivalAt).getTime() - new Date(b.arrivalAt).getTime();
    if (byArrival !== 0) return byArrival;
    return new Date(a.departureAt).getTime() - new Date(b.departureAt).getTime();
  });

  const groups: MatchedOperation[][] = [];
  let groupIndex = 0;
  let prevLatLng: string | null = null;
  const indexByKey = new Map<string, number>();
  matched.forEach((operation, i) => {
    const prev = i > 0 ? matched[i - 1] : null;
    const latlng = `${operation.lat}-${operation.lng}`;
    if (prev?.departureAt !== operation.departureAt
      || prev?.cardIndex !== operation.cardIndex
      || (operation.drivingDistanceMm > 0 && latlng !== prevLatLng)) {
      groupIndex += 1;
      prevLatLng = latlng;
    }
    const key = `${prevLatLng}-${groupIndex}`;
    let at = indexByKey.get(key);
    if (at == null) {
      at = groups.length;
      indexByKey.set(key, at);
      groups.push([]);
    }
    groups[at].push(operation);
  });

  const places: OperationPlace[] = groups.map((ops) => {
    const head = ops[0];
    const tail = ops[ops.length - 1];
    return {
      placeId: `place-${ops.map((o) => o.id).join('-')}`,
      operationIds: ops.map((o) => o.id),
      kind: head.kind,
      locationName: head.locationName,
      position: head.lat != null && head.lng != null ? [head.lat, head.lng] : null,
      arrivalAt: head.arrivalAt,
      departureAt: tail.departureAt,
      // 作業も待機も合計。/plans の PlanningsOperationPlace:324-325 と同じ規則。
      // 待機は operation ごとに別々の値が入る加算前提のデータで
      // (algorithm_request.rb:335 が operation 単位で設定し、
      //  shift.rb:176-178 が勤務全体で合計している)、最大を採ると取りこぼす
      workSeconds: ops.reduce((sum, o) => sum + o.operationDurationSeconds, 0),
      waitSeconds: ops.reduce((sum, o) => sum + o.waitingDurationSeconds, 0),
      shipmentOrderLegRunIds: [...new Set(ops.map((o) => o.shipmentOrderLegRunId))],
      cardIndex: head.cardIndex,
      dispatchOrderIndex: head.dispatchOrderIndex,
    };
  });

  // --- 5. 数量 Map ---
  const quantities = new Map<number, { itemCount: number; weightKg: number; volumeM3: number; shipmentOrderId: number | null }>();
  for (const source of sourceCards) {
    for (const legRun of source.legRuns) {
      if (legRun.id == null || quantities.has(legRun.id)) continue;
      quantities.set(legRun.id, {
        itemCount: legRun.item_count ?? 0,
        weightKg: legRun.item_total_weight_kg ?? 0,
        volumeM3: legRun.item_total_volume_m3 ?? 0,
        shipmentOrderId: legRun.shipment_order_id ?? null,
      });
    }
  }

  return { places, quantities };
}
